import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { ActionsMenuType } from '@/types';
import { ApiClient } from '@/api/apiClient';
import { AxiosError } from 'axios';
import { useMemberships } from './useMemberships';
import { useState } from 'react';

export const useMerchantMemberships = () => {
  const navigate = useNavigate();
  const [showTerminateAlertDialog, setShowTerminateAlertDialog] =
    useState(false);
  const [showDeleteAlertDialog, setShowDeleteAlertDialog] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState('');
  const [membershipId, setMembershipId] = useState('');
  const { data: memberships, refetch, isLoading } = useMemberships();
  const apiClient = ApiClient();

  const terminateMembershipMutation = useMutation(
    async (membershipId: string) => {
      return await apiClient.membershipPlanPurchases.terminateAllMembershipPlansPurchases(
        { membershipId }
      );
    },
    {
      onSuccess: () => {
        refetch();
      },
      onError: (error: AxiosError) => {
        console.error(error);
        //TODO: more robust success/error handling => https://spinspire.monday.com/boards/3397008425/pulses/5353047850
      },
    }
  );

  const deleteMembershipMutation = useMutation(
    async (membershipId: string) => {
      return await apiClient.memberships.deleteMembership({ membershipId });
    },
    {
      onSuccess: () => {
        refetch();
      },
      onError: (error: AxiosError) => {
        console.error(error);
        //TODO: more robust success/error handling => https://spinspire.monday.com/boards/3397008425/pulses/5353047850
      },
    }
  );

  const exportMembershipReportMutation = useMutation(
    async (membershipId: string) => {
      return await apiClient.memberships.exportMembershipReport({
        membershipId,
      });
    },
    {
      onSuccess: async (blob, membershipId) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        const now = new Date().toDateString();
        // auto-downloading the newly created report
        a.href = url;
        a.download = `hub-membership-report-${membershipId}-${now}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        setDownloadStatus(
          'Your membership report is downloading - be patient!'
        );
      },
      onError: (error: AxiosError) => {
        setDownloadStatus('An error occured - please try again');
        console.error(error);
      },
    }
  );

  const handleTerminatedAlertDialogClose = async (value: boolean) => {
    // if the user clicks Accept in the confirmation dialog
    if (value) {
      terminateMembershipMutation.mutate(membershipId);
    }
    setMembershipId('');
    setShowTerminateAlertDialog(false);
  };

  const handleDeleteAlertDialogClose = async (value: boolean) => {
    // if the user clicks Accept in the confirmation dialog
    if (value) {
      deleteMembershipMutation.mutate(membershipId);
    }
    setMembershipId('');
    setShowDeleteAlertDialog(false);
  };

  const onClickMenuItem = (type: ActionsMenuType, membershipId: string) => {
    switch (type) {
      case ActionsMenuType.EDIT_MEMBERSHIP:
        navigate(`${membershipId}`);
        break;
      case ActionsMenuType.DUPLICATE_MEMBERSHIP:
        console.log('Not implemented yet');
        break;
      case ActionsMenuType.DELETE_MEMBERSHIP:
        setMembershipId(membershipId);
        setShowDeleteAlertDialog(true);
        break;
      case ActionsMenuType.TERMINATE_MEMBERSHIP:
        setMembershipId(membershipId);
        setShowTerminateAlertDialog(true);
        break;
      case ActionsMenuType.EXPORT_REPORT:
        exportMembershipReportMutation.mutate(membershipId);
        break;
      default:
        throw new Error(`Unknown action type: ${type}`);
    }
  };

  return {
    memberships,
    isLoading,
    refetch,
    onClickMenuItem,
    terminateMembershipMutation,
    showTerminateAlertDialog,
    setShowTerminateAlertDialog,
    showDeleteAlertDialog,
    downloadStatus,
    setDownloadStatus,
    handleDeleteAlertDialogClose,
    handleTerminatedAlertDialogClose,
    exportMembershipReportMutation,
  };
};
